import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { getUserById } from '../services/userService';

const ADMIN_EMAIL = 'ayushk76596@gmail.com';

const AdminPanel = () => {
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);

    useEffect(() => {
        const checkAdmin = () => {
            const user = auth.currentUser;
            if (!user || user.email !== ADMIN_EMAIL) {
                setIsAdmin(false);
                setLoading(false);
                navigate('/');
                return;
            }
            setIsAdmin(true);
            setLoading(false);
        };

        const unsubscribe = auth.onAuthStateChanged((user) => {
            checkAdmin();
        });

        checkAdmin();
        return () => unsubscribe();
    }, [navigate]);

    // Admin only observes withdrawal requests. Tip confirmation is webhook-only.
    useEffect(() => {
        if (!isAdmin) return;

        const q = query(
            collection(db, 'withdrawalRequests'),
            where('status', '==', 'pending'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            async (snapshot) => {
                const requests = await Promise.all(
                    snapshot.docs.map(async (docSnap) => {
                        const data = docSnap.data();
                        let artist = null;
                        try {
                            artist = await getUserById(data.artistId || data.userId || data.uid);
                        } catch (err) {
                            console.error('Error fetching withdrawal requester:', err);
                        }
                        return {
                            id: docSnap.id,
                            ...data,
                            artistName: artist?.name || artist?.displayName || data.artistName || 'Unknown artist',
                        };
                    })
                );
                setWithdrawalRequests(requests);
            },
            (err) => {
                console.error('Error loading withdrawal requests:', err);
                setWithdrawalRequests([]);
            }
        );

        return () => unsubscribe();
    }, [isAdmin]);

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return 'N/A';
        }
    };

    const confirmWithdrawal = async (request) => {
        try {
            const approveRequest = httpsCallable(functions, 'approveWithdrawalRequest');
            await approveRequest({ requestId: request.id });
        } catch (err) {
            console.error('Failed to confirm withdrawal:', err);
            alert(err?.message || 'Unable to confirm withdrawal');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">
                <div>Loading...</div>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
                    <p>You don't have permission to access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0e0e0e] text-white p-4">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-red-500">Admin Panel</h1>
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600"
                    >
                        Back to App
                    </button>
                </div>

                <div className="mb-6 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 text-blue-200">
                    Tip confirmations are handled automatically by the Razorpay webhook.
                    This panel only displays withdrawal requests.
                </div>

                <div className="space-y-4">
                        {withdrawalRequests.length === 0 ? (
                            <div className="text-center py-12 text-gray-400">
                                No pending withdrawal requests
                            </div>
                        ) : (
                            withdrawalRequests.map((request) => (
                                <div
                                    key={request.id}
                                    className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-4"
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="text-lg font-semibold">
                                                ₹{Number(request.amount || 0).toLocaleString('en-IN')}
                                            </div>
                                            <div className="text-sm text-gray-400 mt-1">
                                                Artist: {request.artistName}
                                            </div>
                                            {request.upiId && (
                                                <div className="text-sm text-gray-300 mt-2">
                                                    UPI: {request.upiId}
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-500 mt-2">
                                                Requested: {formatDate(request.createdAt)}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => confirmWithdrawal(request)}
                                            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-500"
                                        >
                                            Confirm Withdrawal
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
            </div>
        </div>
    );
};

export default AdminPanel;
